import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Button from "../Button";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";
import { howItWorksSteps } from "../../content/home-sections";

// Act 3a of the story site: the mechanism.
//
// Copy and icons come from content/home-sections.ts unchanged. That content is
// already written, on-brand and reviewed; the revamp is a change of
// COMPOSITION, not a licence to rewrite working copy.
//
// ONE DELIBERATE DEPARTURE from that data: each step carries a `rotate` field
// ("tailwind rotate class for the tilted-card look") and this section ignores
// it in favour of vertical stagger. A measured teardown of plysocial.com and
// timeleft.com on 2026-07-31 found ZERO rotated elements on either site; what
// reads as tilt in a screenshot of Timeleft is alternate cards pushed down.
// Stagger gives the same liveliness without the slightly homemade look that
// rotation carries, so `rotate` is left in the data for the existing Home page
// (which still uses it) rather than removed.

// GROUND: vibrant teal, not the charcoal `bg-ink` this used first. Against the
// coral, gold and mint elsewhere on the page that charcoal read as a dull grey
// hole rather than a deliberate dark chapter. Teal is the app's own accent and
// already appears as the single cool note inside the mesh gradient, so it is
// in-palette; the light tinted step cards also pop harder on it than on grey.
const StepsSection: React.FC = () => (
  <section className="relative w-full ground-teal py-24 md:py-32 px-6 overflow-hidden">
    <Doodle
      name="squiggle"
      size={110}
      rotate={-6}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[6%] top-[12%] text-salmon/35"
    />
    <Doodle
      name="loop"
      size={72}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[-2%] max-md:left-[5%] bottom-[10%] text-[#f9f9f7]/20"
    />
    <div className="max-w-6xl mx-auto relative">
      <div className="max-w-2xl mb-14 md:mb-20 md:ml-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-salmon mb-4">
          How it works
        </p>
        <Reveal y={20}>
          <h2
            className="font-headline text-[#f9f9f7] tracking-[-0.025em] leading-[1.16] text-[clamp(2rem,5.8vw,3.6rem)] text-balance"
            style={{ fontWeight: 700 }}
          >
            The plan comes first. The{" "}
            <Highlighter tone="coral">group chat follows.</Highlighter>
          </h2>
        </Reveal>
        <Reveal y={14} delay={110}>
          <p className="mt-5 text-lg text-[#f9f9f7]/75 leading-relaxed">
            Not the other way round. That single inversion is why people end up
            meeting rather than scrolling.
          </p>
        </Reveal>
      </div>

      <ol className="grid gap-5 md:grid-cols-3 list-none">
        {howItWorksSteps.map((step, i) => {
          const { Icon } = step;
          return (
            <Reveal
              key={step.n}
              as="li"
              y={26}
              delay={i * 110}
              // Vertical stagger, the measured alternative to rotation. Only
              // from md up: on a phone the cards are already a single column
              // and an offset would just read as a broken margin.
              className={i === 1 ? "md:mt-10" : i === 2 ? "md:mt-20" : ""}
            >
              <div
                className="h-full rounded-3xl p-7 md:p-8 border border-hairline/60"
                style={{ background: step.bg }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-ink text-cream text-sm font-bold tabular-nums">
                    {step.n}
                  </span>
                  <Icon size={20} className="text-ink/55" aria-hidden="true" />
                </div>
                <h3
                  className="font-headline text-ink text-2xl md:text-[1.7rem] tracking-[-0.02em] mb-3"
                  style={{ fontWeight: 700 }}
                >
                  {step.title}
                </h3>
                <p className="text-ink/70 leading-relaxed">{step.body}</p>
              </div>
            </Reveal>
          );
        })}
      </ol>

      <Reveal
        y={16}
        delay={160}
        className="mt-14 flex justify-center relative z-10"
      >
        <Link to="/download">
          <Button variant="primary" size="lg">
            Get the app
            <ArrowRight size={16} className="ml-2 inline" />
          </Button>
        </Link>
      </Reveal>
    </div>
  </section>
);

export default StepsSection;
